import { apiRequest } from "./api.client";

// ── Types ─────────────────────────────────────────────────────────────────────

export type ClinicDocType =
  | "drug_license"
  | "clinical_establishment_reg"
  | "abdm_registration"
  | "owner_aadhaar"
  | "owner_pan"
  | "medical_director_nmc"
  // Business-registration docs — also managed from Settings → Compliance &
  // Verification (all optional; none gate prescribing).
  | "incorporation"
  | "gst"
  | "clinic_registration"
  | "msme"
  | "other_regulatory";

export type ClinicDocStatus = "submitted" | "verified" | "rejected";

export interface ClinicDocument {
  clinic_doc_id: string;
  clinic_id: string;
  document_type: ClinicDocType;
  s3_key: string;
  status: ClinicDocStatus;
  rejection_reason: string | null;
  verified_at: string | null;
  verified_by: string | null;
  superseded_by: string | null;
  view_url?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ClinicDocPresignedEntry {
  upload_url: string | null;
  s3_key: string;
}

export interface ClinicDocPresignedUrlSet {
  // Keyed by document_type → presigned PUT entry (see backend ClinicDocPresignedUrlSet).
  entries: Partial<Record<ClinicDocType, ClinicDocPresignedEntry>>;
  s3_enabled: boolean;
}

export interface ClinicDocConfirmItem {
  document_type: ClinicDocType;
  s3_key: string;
}

// ── API ───────────────────────────────────────────────────────────────────────

export const ComplianceAPI = {
  listDocuments(clinicId: string): Promise<ClinicDocument[]> {
    return apiRequest<ClinicDocument[]>(`/clinics/${clinicId}/documents`);
  },

  getPresignedUrls(clinicId: string): Promise<ClinicDocPresignedUrlSet> {
    return apiRequest<ClinicDocPresignedUrlSet>(
      `/clinics/${clinicId}/documents/presigned-urls`
    );
  },

  confirmUploads(
    clinicId: string,
    uploads: ClinicDocConfirmItem[]
  ): Promise<ClinicDocument[]> {
    const uploaded_keys = Object.fromEntries(
      uploads.map((u) => [u.document_type, u.s3_key])
    );
    return apiRequest<ClinicDocument[]>(`/clinics/${clinicId}/documents`, {
      method: "POST",
      body: JSON.stringify({ uploaded_keys }),
    });
  },

  getViewUrl(
    clinicId: string,
    docType: ClinicDocType
  ): Promise<string> {
    return apiRequest<string>(
      `/clinics/${clinicId}/documents/${docType}/view-url`
    );
  },

  verifyDocument(
    clinicId: string,
    clinicDocId: string
  ): Promise<ClinicDocument> {
    return apiRequest<ClinicDocument>(
      `/clinics/${clinicId}/documents/${clinicDocId}/verify`,
      { method: "POST" }
    );
  },

  rejectDocument(
    clinicId: string,
    clinicDocId: string,
    reason: string
  ): Promise<ClinicDocument> {
    return apiRequest<ClinicDocument>(
      `/clinics/${clinicId}/documents/${clinicDocId}/reject`,
      {
        method: "POST",
        body: JSON.stringify({ reason }),
      }
    );
  },

  updateAbdmRegistrationId(abdmRegistrationId: string): Promise<void> {
    return apiRequest<void>(`/clinic/registration-ids`, {
      method: "PUT",
      body: JSON.stringify({ abdm_registration_id: abdmRegistrationId }),
    });
  },
};

// ── Onboarding / business compliance docs (clinic_compliance_documents) ────────

export type OnboardingDocType =
  | "incorporation"
  | "gst"
  | "clinic_registration"
  | "msme"
  | "other_regulatory";

export type OnboardingDocStatus = "not_uploaded" | "submitted" | "verified" | "rejected";

export interface OnboardingComplianceDoc {
  id: string | null;
  doc_type: OnboardingDocType;
  label: string;
  status: OnboardingDocStatus;
  rejection_reason: string | null;
  view_url: string | null;
  updated_at: string | null;
}

export interface OnboardingPresignedUploadUrl {
  upload_url: string | null;  // null when S3_ENABLED=false
  s3_key: string;
}

export const OnboardingComplianceAPI = {
  listDocuments(clinicId: string): Promise<OnboardingComplianceDoc[]> {
    return apiRequest<OnboardingComplianceDoc[]>(
      `/clinics/${clinicId}/compliance-documents`
    );
  },

  getPresignedUploadUrl(
    clinicId: string,
    docType: OnboardingDocType
  ): Promise<OnboardingPresignedUploadUrl> {
    return apiRequest<OnboardingPresignedUploadUrl>(
      `/clinics/${clinicId}/compliance-documents/${docType}/presigned-upload-url`
    );
  },

  confirmUpload(
    clinicId: string,
    docType: OnboardingDocType,
    s3Key: string
  ): Promise<OnboardingComplianceDoc> {
    return apiRequest<OnboardingComplianceDoc>(
      `/clinics/${clinicId}/compliance-documents/${docType}`,
      {
        method: "POST",
        body: JSON.stringify({ s3_key: s3Key }),
      }
    );
  },

  getViewUrl(clinicId: string, docType: OnboardingDocType): Promise<string> {
    return apiRequest<string>(
      `/clinics/${clinicId}/compliance-documents/${docType}/view-url`
    );
  },
};
